import { useRef } from 'react';

import { cn } from '../helpers/cn';

export type TabNavItem<T extends string> = {
  value: T;
  label: string;
};

export type TabNavProps<T extends string> = {
  // The tabs to render, in display order.
  tabs: TabNavItem<T>[];
  // The value of the currently active tab.
  activeTab: T;
  // Called with the tab value when a tab is selected.
  onTabChange: (value: T) => void;
  // Accessible label for the tablist (e.g. "Session categories").
  ariaLabel?: string;
  // Extra classes for the tablist container.
  className?: string;
};

// Reusable segmented pill tab navigator. Generic over the tab value type so
// each page can pass its own union (e.g. session categories) and keep
// `activeTab`/`onTabChange` fully typed.
function TabNav<T extends string>({ tabs, activeTab, onTabChange, ariaLabel, className }: TabNavProps<T>) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Roving arrow-key navigation between tabs, wrapping at either end.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') {
      return;
    }
    e.preventDefault();
    const direction = e.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (index + direction + tabs.length) % tabs.length;
    const nextTab = tabs[nextIndex];
    onTabChange(nextTab.value);
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'flex flex-nowrap gap-3 overflow-x-auto pb-2',
        '[scrollbar-color:transparent_transparent] [scrollbar-width:thin] focus-within:[scrollbar-color:var(--color-grey-300)_transparent] hover:[scrollbar-color:var(--color-grey-300)_transparent]',
        '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-transparent focus-within:[&::-webkit-scrollbar-thumb]:bg-grey-300 hover:[&::-webkit-scrollbar-thumb]:bg-grey-300 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:h-1.5',
        className
      )}
    >
      {tabs.map((tab, index) => {
        const isActive = tab.value === activeTab;
        return (
          <button
            key={tab.value}
            ref={(el) => (tabRefs.current[index] = el)}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onTabChange(tab.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={cn(
              'shrink-0 whitespace-nowrap rounded-full px-6 py-2.5 font-[Poppins] text-sm font-semibold shadow-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-green-900 focus-visible:ring-offset-2',
              isActive ? 'bg-green-900 text-white' : 'bg-white text-grey-600 hover:bg-grey-100'
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export default TabNav;
